<!--
Usage:
  Multi-selection dropdown component with label and customizable options.
  Supports data-driven options and Angular component projection.
  Dropdown remains open after selections to allow multiple choices.

Required inputs:
  label: string - Label text for the multiselect component

Optional inputs:
  placeholder: string - Text shown when no options are selected (default: 'Select options')
  labelPosition: 'top' | 'left' | 'right' | 'hide' - Position of label relative to select (default: 'top')
  size: 'default' | 'compact' - Size variant of the component (default: 'default')
  showCheckbox: boolean - Display checkboxes next to options (default: false)
  showSelectAll: boolean - Show 'Select All' button in dropdown (default: false)
  showUnselectAll: boolean - Show 'Unselect All' button in dropdown (default: false)
  showSearch: boolean - Show search/filter input in dropdown (default: false)
  options: MultiselectItem<T>[] - Array of option objects with structure:
    {id: string | number, value: T, label: string, disabled?: boolean}
  optgroups: MultiselectOptgroup<T>[] - Array of grouped options with structure:
    {label: string, options: MultiselectItem<T>[]}
    Note: Optgroup headers are clickable to select/deselect all options in the group

Form Control:
  - Use with formControlName or formControl directives
  - Required state is determined by Validators.required on the form control
  - Disabled state is controlled by the form control's disabled state

Examples:
  Data-driven:
    <ui-multiselect [options]="myOptions" label="Choose options" formControlName="field" />
    <ui-multiselect [optgroups]="myOptgroups" label="Choose options" [showCheckbox]="true" formControlName="field" />

  Component projection:
    <ui-multiselect label="Choose options" [showCheckbox]="true" formControlName="field">
      <ui-optgroup label="Deciduous">
        <ui-option [value]="{name: 'elm', branches: 12}">Elm</ui-option>
        <ui-option [value]="{name: 'ash', branches: 5}">Ash</ui-option>
      </ui-optgroup>
    </ui-multiselect>
-->

<div [class]="`${labelPosition()} component-wrapper`">
  <label [for]="id" class="label-text">{{ label() }}</label>
  <div class="multiselect-container component-container">
    <button
      type="button"
      [id]="id"
      [disabled]="disabled()"
      [class.size]="size() === 'compact'"
      (click)="toggleDropdown()"
      (keydown)="handleTriggerKeydown($event)"
      (blur)="handleOnBlur()"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-haspopup]="'listbox'"
      [attr.aria-expanded]="isOpen()"
      [attr.aria-controls]="dropdownId"
      [attr.aria-activedescendant]="activeDescendantId()"
      [attr.aria-required]="isRequired() || null"
      [attr.aria-describedby]="instructionsId"
      class="multiselect-trigger component-trigger"
      #triggerButton
    >
      <span class="selected-text" aria-hidden="true">{{ selectedLabels() }}</span>
      <span class="dropdown-icon" [class.open]="isOpen()" aria-hidden="true"></span>
    </button>

    @if (isOpen()) {
      <div
        [id]="dropdownId"
        class="multiselect-dropdown component-dropdown"
        role="listbox"
        [attr.aria-multiselectable]="true"
        [attr.aria-labelledby]="id"
        (keydown)="handleDropdownKeydown($event)"
        #dropdown
      >
        <div class="dropdown-header">
          @if (showSearch()) {
            <div class="multiselect-search search-container">
              <input
                type="search"
                class="search-input"
                [placeholder]="searchPlaceholder()"
                [value]="searchTerm()"
                (input)="onSearchInput($event)"
                (click)="$event.stopPropagation()"
                aria-label="Search options"
              />
            </div>
          }
          @if (showSelectAll() || showUnselectAll()) {
            <div class="multiselect-actions">
              @if (showSelectAll()) {
                <button type="button" class="button" (click)="selectAll()" [attr.aria-label]="'Select all options'">
                  Select All
                </button>
              }
              @if (showUnselectAll()) {
                <button
                  type="button"
                  class="button deselect"
                  (click)="unselectAll()"
                  [attr.aria-label]="'Unselect all options'"
                >
                  Unselect All
                </button>
              }
            </div>
          }
        </div>
        @if (filteredOptgroups().length) {
          @for (optgroup of filteredOptgroups(); track optgroup.label) {
            <div class="optgroup" role="group" [attr.aria-label]="optgroup.label">
              <div
                class="optgroup-header dropdown-item"
                (click)="toggleOptgroup(optgroup)"
                (keydown)="handleOptgroupKeydown($event, optgroup)"
                role="button"
                [attr.tabindex]="0"
                [attr.aria-label]="'Select all ' + optgroup.label"
              >
                @if (showCheckbox()) {
                  <input
                    type="checkbox"
                    [checked]="isOptgroupAllSelected(optgroup)"
                    [indeterminate]="isOptgroupIndeterminate(optgroup)"
                    tabindex="-1"
                    aria-hidden="true"
                  />
                }
                {{ optgroup.label }}
              </div>
              @for (option of optgroup.options; track option.id) {
                <div
                  [id]="'option-' + option.id"
                  class="option dropdown-item"
                  [class.selected]="isSelected(option)"
                  [class.disabled]="option.disabled"
                  (click)="toggleOption(option)"
                  (keydown)="handleDropdownKeydown($event, option)"
                  role="option"
                  [attr.aria-selected]="isSelected(option)"
                  [attr.aria-disabled]="option.disabled"
                  [attr.tabindex]="option.disabled ? -1 : 0"
                >
                  @if (showCheckbox()) {
                    <input
                      type="checkbox"
                      [checked]="isSelected(option)"
                      [disabled]="option.disabled"
                      tabindex="-1"
                      aria-hidden="true"
                    />
                  }
                  {{ option.label }}
                </div>
              }
            </div>
          }
        } @else if (filteredOptions().length) {
          @for (option of filteredOptions(); track option.id) {
            <div
              [id]="'option-' + option.id"
              class="option dropdown-item"
              [class.selected]="isSelected(option)"
              [class.disabled]="option.disabled"
              (click)="toggleOption(option)"
              (keydown)="handleDropdownKeydown($event, option)"
              role="option"
              [attr.aria-selected]="isSelected(option)"
              [attr.aria-disabled]="option.disabled"
              [attr.tabindex]="option.disabled ? -1 : 0"
            >
              @if (showCheckbox()) {
                <input
                  type="checkbox"
                  [checked]="isSelected(option)"
                  [disabled]="option.disabled"
                  tabindex="-1"
                  aria-hidden="true"
                />
              }
              {{ option.label }}
            </div>
          }
        } @else {
          <!-- needed for cases like rendering a loader -->
          <div #projectedContentWrapper>
            <ng-content></ng-content>
          </div>
          @if (!hasProjectedContent() && emptyMultiselectMessage()) {
            <div class="no-results" role="status" aria-live="polite">{{ emptyMultiselectMessage() }}</div>
          }
        }
      </div>
    }

    <div [id]="instructionsId" class="visually-hidden">
      Multiple selection enabled. Use Space or Enter to toggle options. Press Escape to close.
    </div>

    <div [id]="liveRegionId" class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {{ liveMessage() }}
    </div>
  </div>
</div>
